/**
 * Public Works Lead Finder — government bids, RFPs, RFQs, public construction.
 * Single page with internal tabs (Dashboard / Search / Pipeline / Alerts / Sources).
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Landmark, Search, FileText, Bell, KanbanSquare, Database, Loader2, MapPin,
  Calendar, DollarSign, Building2, ExternalLink, Sparkles, AlertCircle,
  Plus, Trash2, Download, CheckCircle2, Clock, Briefcase, Hash, Hammer, X,
  RefreshCw,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import AppHeader from "../components/AppHeader";
import ProcurementGeo from "../components/ProcurementGeo";
import { EmailAction, PhoneAction } from "../components/ContactActions";

const govCtx = (opp) => ({
  name: opp.contact_name, opportunity: opp.title, agency: opp.agency,
  city: opp.city, state: opp.state, value: opp.estimated_value,
  source: opp.source, trade: opp.trade_category,
  description: (opp.description || "").slice(0, 500),
});
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../auth/AuthContext";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useTrialLock } from "../hooks/useTrialLock";
import { LockedField, blockExportIfTrial, rememberSearchContext } from "../components/TrialLock";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MaterialsSection, ResearchSection } from "../components/GovOppExtras";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "dashboard",    icon: Database,       en: "Dashboard",         pt: "Painel" },
  { id: "search",       icon: Search,         en: "Search",            pt: "Buscar" },
  { id: "sic-naics",    icon: Hash,           en: "SIC & NAICS",       pt: "SIC e NAICS" },
  { id: "construction", icon: Hammer,         en: "Construction Leads", pt: "Leads de Obras" },
  { id: "pipeline",     icon: KanbanSquare,   en: "Pipeline",          pt: "Pipeline" },
  { id: "alerts",       icon: Bell,           en: "Alerts",            pt: "Alertas" },
  { id: "sources",      icon: FileText,       en: "Sources",           pt: "Fontes" },
];

const STAGE_LABELS = {
  new: { en: "New", pt: "Novo", color: "bg-blue-100 text-blue-700" },
  reviewing: { en: "Reviewing", pt: "Em análise", color: "bg-purple-100 text-purple-700" },
  interested: { en: "Interested", pt: "Interessado", color: "bg-cyan-100 text-cyan-700" },
  preparing_bid: { en: "Preparing bid", pt: "Preparando proposta", color: "bg-amber-100 text-amber-700" },
  submitted: { en: "Submitted", pt: "Submetido", color: "bg-indigo-100 text-indigo-700" },
  won: { en: "Won", pt: "Ganho", color: "bg-emerald-100 text-emerald-700" },
  lost: { en: "Lost", pt: "Perdido", color: "bg-rose-100 text-rose-700" },
};

const URGENCY_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

function fmtMoney(v) {
  if (!v) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch (_e) { return iso.slice(0, 10); }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso) - new Date();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch (_e) { return null; }
}


// Iter129 — never render a dead "#" portal link: when the opportunity has no
// source_url fall back to a targeted web search for it.
const portalHref = (title, agency, url) =>
  url || `https://www.google.com/search?q=${encodeURIComponent(`${title || ""} ${agency || ""} bid`)}`;

export default function PublicWorks() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const { user } = useAuth();
  const isMaster = !!(user && (user.role === "admin" || (user.email || "").toLowerCase() === "master@agentelead.pro"));
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ trades: [], opportunity_types: [], sources: [], priority_states: [], secondary_states: [] });
  const { onTrial } = useTrialLock();

  useEffect(() => {
    axios.get(`${API}/public-works/meta`).then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="public-works-page">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="public-works-header"
          title="Procurement Scanner"
          subtitle={isPt ? "Encontre obras públicas, licitações e RFPs antes da concorrência." : "Find public construction bids, RFPs and DOT projects before competitors."}
          logo={
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-[#1B3A8A] to-[#0A1233] text-[#E5C572] flex items-center justify-center shrink-0">
              <Landmark className="w-5 h-5" />
            </div>
          }
          mobileMenu={
            <div className="flex items-center gap-1 px-2 py-1.5">
              <ClaireHelpButton testid="public-works-mobile"
                question="Como uso o Procurement Scanner?" />
              <ThemeToggle />
              <LangToggle />
            </div>
          }
        >
          <ClaireHelpButton testid="public-works"
            question="Como uso o Procurement Scanner — buscar licitações e obras públicas por estado e categoria?" />
          <ThemeToggle /><LangToggle />
        </AppHeader>

        <div className="px-3 sm:px-6 lg:px-8 pt-4 sm:pt-5">
          <nav className="flex items-center gap-1 border-b border-[var(--al-border)]" data-testid="pwf-tabs">
            {TABS.filter((tt) => tt.id !== "sources" || isMaster).map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id} type="button"
                  data-testid={`pwf-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`relative px-4 py-2.5 text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${
                    active ? "text-[#1B3A8A] border-b-2 border-[#C9A24E] -mb-px" : "text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {isPt ? t.pt : t.en}
                </button>
              );
            })}
          </nav>
        </div>

        <main className="px-8 py-7">
          {tab === "dashboard" && <DashboardTab isPt={isPt} onJump={setTab} meta={meta} />}
          {tab === "search" && <SearchTab isPt={isPt} meta={meta} onTrial={onTrial} />}
          {tab === "sic-naics" && <SicNaicsTab isPt={isPt} onTrial={onTrial} />}
          {tab === "construction" && <ConstructionLeadsTab isPt={isPt} onTrial={onTrial} />}
          {tab === "pipeline" && <PipelineTab isPt={isPt} />}
          {tab === "alerts" && <AlertsTab isPt={isPt} meta={meta} />}
          {tab === "sources" && <SourcesTab isPt={isPt} />}
        </main>
      </div>
    </div>
  );
}

// --- Dashboard --------------------------------------------------------------

function DashboardTab({ isPt, onJump, meta }) {
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [pulse, setPulse] = useState(false);
  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public-works/dashboard`);
      setData(r.data);
      setLastUpdated(Date.now());
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
    } catch (_e) { setData({}); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useAutoRefresh(reload, 30000, { defaultEnabled: true });
  if (!data) return <Spinner />;

  const stats = [
    { label: isPt ? "Oportunidades totais" : "Total opportunities",  v: data.total || 0, icon: FileText, color: "#1B3A8A", jump: "search" },
    { label: isPt ? "Novas esta semana" : "New this week",          v: data.new_this_week || 0, icon: Sparkles, color: "#10B981", jump: "search" },
    { label: isPt ? "Fechando em 7 dias" : "Closing in 7 days",     v: data.closing_7_days || 0, icon: Clock, color: "#F59E0B", jump: "pipeline" },
    { label: isPt ? "Estados monitorados" : "States monitored",      v: data.states_monitored || 0, icon: MapPin, color: "#A855F7", jump: "search" },
  ];
  const sinceSec = Math.max(0, Math.floor((Date.now() - lastUpdated) / 1000));

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#1B3A8A] to-[#0A1233] text-white rounded-2xl p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5C572] font-bold mb-2">
            AgenteLead Pro · Procurement Scanner
          </div>
          <h2 className="text-[22px] lg:text-[26px] font-bold leading-tight">
            {isPt
              ? "Encontre oportunidades públicas antes da concorrência."
              : "Find public construction opportunities before your competitors do."}
          </h2>
          <p className="text-white/70 text-[13px] mt-1.5 max-w-2xl">
            {isPt
              ? "Buscas em Bids, RFPs, RFQs, DOT projects e portais de aquisição pública por estado, categoria e tipo de serviço."
              : "Search government bids, RFPs, RFQs, DOT projects, and public works portals by state, trade category, and service type."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <button
            type="button" data-testid="pwf-cta-find"
            onClick={() => onJump("search")}
            className="bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] font-bold px-5 py-2.5 rounded-lg text-[13px] inline-flex items-center gap-1.5"
          >
            <Search className="w-4 h-4" /> {isPt ? "Buscar leads públicos" : "Find Public Leads"}
          </button>
          <button
            type="button" data-testid="pwf-cta-alert"
            onClick={() => onJump("alerts")}
            className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-2.5 rounded-lg text-[13px] inline-flex items-center gap-1.5 border border-white/20"
          >
            <Bell className="w-4 h-4" /> {isPt ? "Criar alerta" : "Create alert"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pwf-dashboard-kpis">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} role="button" tabIndex={0}
              onClick={() => onJump(s.jump || "search")}
              title={isPt ? "Clique para abrir" : "Click to open"}
              className={`al-card p-4 transition-all cursor-pointer hover:shadow-md hover:-translate-y-[1px] ${pulse ? "ring-2 ring-[#10B981]/40" : ""}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">{s.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform ${pulse ? "animate-pulse scale-110" : ""}`} style={{ background: `${s.color}1A`, color: s.color }}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-[28px] font-bold leading-none tabular-nums">{s.v}</div>
              <div className="text-[10px] text-[var(--al-text-muted)] mt-1 inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {isPt ? "ao vivo" : "live"} · {sinceSec}s
              </div>
            </div>
          );
        })}
      </div>

      <ProcurementGeo isPt={isPt} trades={meta?.trades || []} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="al-card p-5 lg:col-span-2">
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-3">
            {isPt ? "Categorias mais ativas" : "Most active trade categories"}
          </div>
          <div className="space-y-2">
            {(data.top_trades || []).map((t) => {
              const max = Math.max(...(data.top_trades || []).map((x) => x.count));
              const pct = max ? (t.count / max) * 100 : 0;
              return (
                <div key={t.trade}>
                  <div className="flex items-center justify-between text-[12.5px] mb-1">
                    <span className="font-medium">{t.trade}</span>
                    <span className="text-[var(--al-text-muted)]">{t.count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[var(--al-row-alt)] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#1B3A8A] to-[#C9A24E]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="al-card p-5">
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-3">
            {isPt ? "Valor total estimado" : "Estimated total value"}
          </div>
          <div className="text-[34px] font-bold text-[#10B981]">{fmtMoney(data.estimated_total_value || 0)}</div>
          <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1">
            {isPt ? "Soma de oportunidades com valor declarado" : "Sum of opportunities with declared value"}
          </div>
          <div className="mt-4 pt-4 border-t border-[var(--al-border)]">
            <div className="text-[11px] text-[var(--al-text-muted)] mb-1.5">{isPt ? "Estados prioritários" : "Priority states"}</div>
            <div className="flex flex-wrap gap-1.5">
              {(data.priority_states || []).map((s) => (
                <span key={s} className="text-[10.5px] font-bold px-2 py-1 rounded bg-[#C9A24E]/15 text-[#92660C]">{s}</span>
              ))}
              {(data.secondary_states || []).map((s) => (
                <span key={s} className="text-[10.5px] font-bold px-2 py-1 rounded bg-[var(--al-row-alt)] text-[var(--al-text-muted)]">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Search -----------------------------------------------------------------

function SearchTab({ isPt, meta, onTrial }) {
  const [filters, setFilters] = useState({
    state: "", city: "", trade: "", keyword: "", naics: "",
    opportunity_type: "", source: "", deadline_within: 0, min_fit: 0,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // selected opp for modal
  // Iter327 · Delete + auto-sync status
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [purgeDays, setPurgeDays] = useState(30);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const nx = new Set(prev);
    if (nx.has(id)) nx.delete(id); else nx.add(id);
    return nx;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(items.map((x) => x.id)));

  const loadSyncStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API}/public-works/opportunities/sync-status`);
      setSyncStatus(data);
    } catch (_e) { /* silent */ }
  };

  const search = async () => {
    rememberSearchContext({
      sector: filters.trade,
      city: filters.city || filters.state,
      keyword: filters.keyword,
    });
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== 0));
      const { data } = await axios.get(`${API}/public-works/opportunities`, { params });
      setItems(data.items || []);
      clearSelection();
    } finally { setLoading(false); }
  };

  // Iter327 · Delete helpers with undo (6s soft-delete pattern)
  const deleteOne = async (opp) => {
    // Optimistic remove, undo restores
    setItems((prev) => prev.filter((x) => x.id !== opp.id));
    try {
      await axios.delete(`${API}/public-works/opportunities/${opp.id}`);
      toast.success(
        isPt ? "Oportunidade excluída" : "Opportunity deleted",
        {
          duration: 6000,
          action: {
            label: isPt ? "Desfazer" : "Undo",
            onClick: async () => {
              try {
                await axios.post(`${API}/public-works/opportunities/${opp.id}/restore`);
                await search();
                toast.success(isPt ? "Restaurada" : "Restored");
              } catch { toast.error("Restore failed"); }
            },
          },
        },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      await search();
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (!window.confirm(
      isPt
        ? `Excluir ${ids.length} oportunidade(s)? Pode desfazer nos próximos 6 segundos.`
        : `Delete ${ids.length} opportunity(ies)? You can undo in the next 6 seconds.`)) return;
    setDeleting(true);
    try {
      const r = await axios.post(
        `${API}/public-works/opportunities/bulk-delete`, { ids });
      toast.success(
        isPt
          ? `${r.data.modified} excluídas`
          : `${r.data.modified} deleted`,
        {
          duration: 6000,
          action: {
            label: isPt ? "Desfazer" : "Undo",
            onClick: async () => {
              try {
                await Promise.all(ids.map((id) => axios.post(
                  `${API}/public-works/opportunities/${id}/restore`)));
                await search();
              } catch { toast.error("Undo failed"); }
            },
          },
        },
      );
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setDeleting(false); }
  };

  const purgeOlderThan = async (hard) => {
    if (!window.confirm(
      isPt
        ? `${hard ? "Excluir PERMANENTEMENTE" : "Soft-delete"} entradas com posted_date anterior a ${purgeDays} dias?`
        : `${hard ? "PERMANENTLY delete" : "Soft-delete"} entries older than ${purgeDays} days?`)) return;
    try {
      const r = await axios.post(
        `${API}/public-works/opportunities/purge-older-than`,
        { days: purgeDays, field: "posted_date", hard,
          include_soft_deleted: hard });
      const n = r.data.deleted ?? r.data.soft_deleted ?? 0;
      toast.success(
        isPt ? `${n} entradas removidas` : `${n} entries removed`);
      setPurgeOpen(false);
      await search();
      await loadSyncStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    }
  };

  const triggerSyncNow = async () => {
    try {
      toast.info(isPt ? "Sincronizando…" : "Syncing…");
      const r = await axios.post(
        `${API}/public-works/samgov-cron/run`, {}, { timeout: 180000 });
      const ins = r.data?.result?.inserted ?? 0;
      toast.success(
        isPt ? `${ins} novas oportunidades` : `${ins} new opportunities`);
      await search();
      await loadSyncStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncSamGov = async () => {
    setSyncing(true);
    try {
      // Apify gov contracts actor — fetches real SAM.gov data via Apify's
      // (whitelisted) egress. Replaces the direct SAM.gov call which is
      // blocked from cloud egress IPs.
      const body = {
        trade: filters.trade || null,
        state: filters.state || null,
        keywords: filters.keyword ? [filters.keyword] : null,
        max_results_per_source: 25,
      };
      const { data } = await axios.post(`${API}/public-works/apify-gov/sync`, body, { timeout: 180000 });
      const msg = isPt
        ? `SAM.gov: ${data.inserted} novas, ${data.updated} atualizadas (${data.fetched} buscadas)`
        : `SAM.gov: ${data.inserted} new, ${data.updated} updated (${data.fetched} fetched)`;
      if (data.fetched === 0) {
        toast.info(isPt
          ? "Nenhuma oportunidade nova com esses filtros. Tente outra trade ou estado."
          : "No new opportunities for these filters. Try a different trade or state.");
      } else {
        toast.success(msg);
      }
      await search();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(isPt ? `SAM.gov falhou: ${detail}` : `SAM.gov failed: ${detail}`);
    } finally { setSyncing(false); }
  };

  useEffect(() => { search(); loadSyncStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const exportCsv = () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar CSV" : "export CSV")) return;
    if (items.length === 0) return;
    const headers = ["title", "agency", "state", "city", "trade_category", "opportunity_type", "source", "posted_at", "deadline", "estimated_value", "fit_score", "urgency_level", "source_url"];
    const rows = items.map((i) => headers.map((h) => `"${String(i[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `public_works_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(isPt ? "CSV baixado" : "CSV downloaded");
  };

  return (
    <div className="space-y-4">
      <UrgentBanner items={items} isPt={isPt} onView={setOpen} />

      {/* Iter327 · Auto-sync status + purge controls */}
      {syncStatus && (
        <div
          className="al-card p-3 flex flex-wrap items-center gap-3 text-[12.5px]"
          data-testid="pwf-sync-status-bar">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded font-semibold ${
            syncStatus.cron_enabled
              ? "bg-emerald-500/10 text-emerald-500"
              : "bg-slate-500/10 text-slate-500"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              syncStatus.cron_enabled ? "bg-emerald-500 animate-pulse" : "bg-slate-500"
            }`} />
            {isPt ? "Auto-sync" : "Auto-sync"} {syncStatus.cron_enabled ? "ON" : "OFF"}
            {syncStatus.cron_enabled && ` · ${String(syncStatus.cron_hour_utc).padStart(2, "0")}:${String(syncStatus.cron_minute_utc || 0).padStart(2, "0")} UTC`}
          </span>
          <span className="text-[var(--al-text-muted)]">
            {isPt ? "Última execução" : "Last run"}:{" "}
            <span className="font-mono">
              {syncStatus.last_run_at
                ? new Date(syncStatus.last_run_at).toLocaleString()
                : "—"}
            </span>
            {syncStatus.last_run_inserted != null && ` · ${syncStatus.last_run_inserted} ${isPt ? "novas" : "new"}`}
            {syncStatus.last_run_apify_fallback_used && ` · Apify fallback`}
          </span>
          <span className="text-[var(--al-text-muted)]">
            {isPt ? "Total" : "Total"}: <span className="font-bold text-[var(--al-text)]">{syncStatus.totals?.active}</span>
            {syncStatus.totals?.deleted > 0 && ` · ${syncStatus.totals.deleted} ${isPt ? "excluídas" : "deleted"}`}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={triggerSyncNow}
              data-testid="pwf-sync-now-btn"
              className="h-8 px-3 rounded-lg border border-[var(--al-border)] text-[11.5px] font-semibold hover:bg-[var(--al-row-alt)] inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3" /> {isPt ? "Sincronizar agora" : "Sync now"}
            </button>
            <button type="button" onClick={() => setPurgeOpen((v) => !v)}
              data-testid="pwf-purge-open-btn"
              className="h-8 px-3 rounded-lg border border-red-400/40 text-red-500 text-[11.5px] font-semibold hover:bg-red-500/10 inline-flex items-center gap-1.5">
              {isPt ? "Limpar antigas…" : "Purge old…"}
            </button>
          </div>
        </div>
      )}

      {purgeOpen && (
        <div className="al-card p-3 flex flex-wrap items-center gap-2 text-[12.5px]"
             data-testid="pwf-purge-panel">
          <span className="font-semibold">
            {isPt ? "Remover entradas com posted_date anterior a" : "Remove entries with posted_date older than"}
          </span>
          <input type="number" min={1} max={3650} value={purgeDays}
            data-testid="pwf-purge-days"
            onChange={(e) => setPurgeDays(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="w-20 h-8 px-2 rounded border border-[var(--al-border)] bg-transparent text-center" />
          <span>{isPt ? "dias" : "days"}</span>
          <button type="button" onClick={() => purgeOlderThan(false)}
            data-testid="pwf-purge-soft"
            className="h-8 px-3 rounded-lg border border-amber-400/40 text-amber-500 text-[11.5px] font-semibold hover:bg-amber-500/10">
            {isPt ? "Soft delete" : "Soft delete"}
          </button>
          <button type="button" onClick={() => purgeOlderThan(true)}
            data-testid="pwf-purge-hard"
            className="h-8 px-3 rounded-lg border border-red-400/40 text-red-500 text-[11.5px] font-semibold hover:bg-red-500/10">
            {isPt ? "Excluir permanentemente" : "Delete permanently"}
          </button>
          <button type="button" onClick={() => setPurgeOpen(false)}
            className="h-8 px-3 text-[11.5px] text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
            {isPt ? "Cancelar" : "Cancel"}
          </button>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="al-card p-3 flex flex-wrap items-center gap-2 text-[12.5px] border-l-4 border-l-[#1B3A8A]"
          data-testid="pwf-bulk-toolbar">
          <span className="font-semibold">
            {selectedIds.size} {isPt ? "selecionadas" : "selected"}
          </span>
          <button type="button" onClick={selectAllVisible}
            data-testid="pwf-select-all"
            className="h-8 px-3 text-[11.5px] hover:underline">
            {isPt ? "Selecionar todas visíveis" : "Select all visible"}
          </button>
          <button type="button" onClick={clearSelection}
            data-testid="pwf-clear-selection"
            className="h-8 px-3 text-[11.5px] hover:underline">
            {isPt ? "Limpar seleção" : "Clear"}
          </button>
          <button type="button" onClick={deleteSelected} disabled={deleting}
            data-testid="pwf-delete-selected"
            className="ml-auto h-8 px-3 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
            {deleting
              ? <><Loader2 className="w-3 h-3 animate-spin" /> {isPt ? "Excluindo…" : "Deleting…"}</>
              : <>{isPt ? `Excluir ${selectedIds.size}` : `Delete ${selectedIds.size}`}</>}
          </button>
        </div>
      )}

      <div className="al-card p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pwf-filters">
        <SelectInput label={isPt ? "Estado" : "State"} value={filters.state}
          onChange={(v) => setFilters({ ...filters, state: v })}
          options={["", ...(meta.priority_states || []), ...(meta.secondary_states || [])]} testid="pwf-filter-state" />
        <TextInput label={isPt ? "Cidade / Condado" : "City / County"} value={filters.city}
          onChange={(v) => setFilters({ ...filters, city: v })} testid="pwf-filter-city" />
        <SelectInput label={isPt ? "Categoria / Trade" : "Trade / Service"} value={filters.trade}
          onChange={(v) => setFilters({ ...filters, trade: v })} options={["", ...(meta.trades || [])]} testid="pwf-filter-trade" />
        <TextInput label={isPt ? "Palavra-chave" : "Keyword"} value={filters.keyword}
          onChange={(v) => setFilters({ ...filters, keyword: v })} testid="pwf-filter-keyword" />
        <TextInput label="NAICS" value={filters.naics}
          onChange={(v) => setFilters({ ...filters, naics: v })} testid="pwf-filter-naics" />
        <SelectInput label={isPt ? "Tipo" : "Type"} value={filters.opportunity_type}
          onChange={(v) => setFilters({ ...filters, opportunity_type: v })} options={["", ...(meta.opportunity_types || [])]} testid="pwf-filter-type" />
        <SelectInput label={isPt ? "Fonte" : "Source"} value={filters.source}
          onChange={(v) => setFilters({ ...filters, source: v })}
          options={[
            { v: "", label: isPt ? "Todas as fontes" : "All sources" },
            ...(meta.sources || []).map((s) => ({ v: s, label: s })),
          ]}
          testid="pwf-filter-source" />
        <SelectInput label={isPt ? "Prazo" : "Deadline"} value={String(filters.deadline_within)}
          onChange={(v) => setFilters({ ...filters, deadline_within: Number(v) })}
          options={[
            { v: "0", label: isPt ? "Qualquer prazo" : "Any deadline" },
            { v: "7", label: isPt ? "Fecha em 7 dias" : "Closing in 7d" },
            { v: "14", label: isPt ? "Fecha em 14 dias" : "Closing in 14d" },
            { v: "30", label: isPt ? "Fecha em 30 dias" : "Closing in 30d" },
          ]} testid="pwf-filter-deadline" />
        <div className="sm:col-span-2 lg:col-span-4 flex flex-wrap items-center gap-2 pt-1 border-t border-[var(--al-border)] -mt-1">
          <button type="button" data-testid="pwf-search-btn" onClick={search}
            className="h-9 px-4 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5" /> {isPt ? "Buscar" : "Search"}
          </button>
          <button type="button" data-testid="pwf-export-csv" onClick={exportCsv} disabled={items.length === 0}
            className="h-9 px-4 rounded-lg border border-[var(--al-border)] text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button type="button" data-testid="pwf-sync-samgov" onClick={syncSamGov} disabled={syncing}
            title={isPt ? "Buscar oportunidades reais em SAM.gov com seus filtros atuais" : "Pull live federal opportunities from SAM.gov using your current filters"}
            className="h-9 px-4 rounded-lg bg-[#0A1233] hover:bg-[#1B3A8A] text-[#E5C572] text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
            {syncing
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {isPt ? "Sincronizando…" : "Syncing…"}</>
              : <><Landmark className="w-3.5 h-3.5" /> Sync SAM.gov</>}
          </button>
          <span className="ml-auto text-[12px] text-[var(--al-text-muted)]" data-testid="pwf-count">
            {loading ? (isPt ? "Carregando…" : "Loading…") : `${items.length} ${isPt ? "oportunidades" : "opportunities"}`}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pwf-results">
        {items.map((opp) => (
          <div key={opp.id} className="relative">
            <label
              className="absolute top-2 left-2 z-10 flex items-center gap-1.5 bg-[var(--al-bg)]/90 backdrop-blur px-1.5 py-1 rounded cursor-pointer text-[11px]"
              data-testid={`pwf-select-${opp.id}`}
              title={isPt ? "Selecionar" : "Select"}>
              <input type="checkbox"
                checked={selectedIds.has(opp.id)}
                onChange={() => toggleSelect(opp.id)}
                className="w-3.5 h-3.5 accent-red-600" />
            </label>
            <button type="button" onClick={() => deleteOne(opp)}
              data-testid={`pwf-delete-${opp.id}`}
              title={isPt ? "Excluir esta oportunidade" : "Delete this opportunity"}
              className="absolute top-2 right-2 z-10 w-6 h-6 rounded bg-red-500/10 hover:bg-red-600 text-red-500 hover:text-white flex items-center justify-center transition">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <OpportunityCard opp={opp} isPt={isPt} onView={setOpen} />
          </div>
        ))}
        {!loading && items.length === 0 && (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)] md:col-span-2 xl:col-span-3">
            {isPt ? "Nenhuma oportunidade encontrada com esses filtros." : "No opportunities match your filters."}
          </div>
        )}
      </div>

      <DetailModal opp={open} onClose={() => setOpen(null)} isPt={isPt} onSaved={search} />
    </div>
  );
}

function UrgentBanner({ items, isPt, onView }) {
  const urgent = (items || [])
    .filter((o) => {
      const d = daysUntil(o.deadline);
      return d !== null && d >= 0 && d <= 7;
    })
    .sort((a, b) => (daysUntil(a.deadline) || 99) - (daysUntil(b.deadline) || 99))
    .slice(0, 4);
  if (urgent.length === 0) return null;
  return (
    <div data-testid="pwf-urgent-banner"
         className="rounded-2xl border-2 border-red-300 bg-gradient-to-r from-red-50 via-red-100 to-amber-50 p-4 flex flex-col lg:flex-row lg:items-center gap-3">
      <div className="flex items-center gap-2.5 shrink-0">
        <div className="w-9 h-9 rounded-lg bg-red-500 text-white flex items-center justify-center animate-pulse">
          <AlertCircle className="w-5 h-5" />
        </div>
        <div className="leading-tight">
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-red-700">{isPt ? "Urgente" : "Urgent"}</div>
          <div className="text-[14px] font-bold text-red-900">
            {urgent.length} {isPt ? "oportunidades fecham em ≤7 dias" : "opportunities close in ≤7 days"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 lg:ml-auto">
        {urgent.map((o) => {
          const d = daysUntil(o.deadline);
          return (
            <button key={o.id} type="button" data-testid={`pwf-urgent-${o.id}`}
              onClick={() => onView(o)}
              className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-[11.5px] font-semibold text-red-800 hover:bg-red-100 hover:border-red-400 inline-flex items-center gap-1.5 transition-colors max-w-[260px]">
              <span className="font-bold tabular-nums">{d}d</span>
              <span className="truncate">{o.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function OpportunityCard({ opp, isPt, onView }) {
  const days = daysUntil(opp.deadline);
  const fit = opp.fit_score || 0;
  // Fit score color band
  const fitTone = fit >= 75 ? "emerald" : fit >= 50 ? "amber" : "rose";
  const fitClass = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
  }[fitTone];
  // Deadline risk
  const deadlineClass = days === null ? "bg-gray-100 text-gray-600" :
    days <= 3 ? "bg-red-100 text-red-700 border-red-300 animate-pulse" :
    days <= 7 ? "bg-red-50 text-red-700 border-red-200" :
    days <= 14 ? "bg-amber-50 text-amber-700 border-amber-200" :
    "bg-emerald-50 text-emerald-700 border-emerald-200";
  // Source reliability
  const rel = opp.source_reliability || (
    ["SAM.gov","USAspending"].includes(opp.source) ? "federal_official" :
    ["FDOT","TxDOT","Caltrans"].includes(opp.source) ? "state_dot" :
    ["BidNet","DemandStar","Bonfire","PlanetBids","OpenGov"].includes(opp.source) ? "aggregator_verified" : "other");
  const relMeta = {
    federal_official: { label: isPt ? "Oficial Federal" : "Federal Official", cls: "bg-blue-50 text-blue-700 border-blue-200", emoji: "★" },
    state_dot: { label: isPt ? "DOT Estadual" : "State DOT", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", emoji: "◆" },
    aggregator_verified: { label: isPt ? "Agregador" : "Aggregator", cls: "bg-purple-50 text-purple-700 border-purple-200", emoji: "•" },
    other: { label: isPt ? "Outra fonte" : "Other", cls: "bg-gray-50 text-gray-600 border-gray-200", emoji: "·" },
  }[rel] || { label: opp.source, cls: "bg-gray-50 text-gray-600 border-gray-200", emoji: "·" };

  return (
    <div data-testid={`pwf-card-${opp.id}`} className="al-card p-4 flex flex-col group hover:shadow-lg hover:border-[#1B3A8A]/40 transition-all duration-200">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className="text-[9.5px] uppercase font-bold tracking-wider text-[#1B3A8A]">{opp.opportunity_type}</span>
            <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded border ${relMeta.cls} inline-flex items-center gap-1`}>
              <span aria-hidden>{relMeta.emoji}</span> {opp.source}
            </span>
          </div>
          <h3 className="text-[14.5px] font-bold leading-snug line-clamp-2 group-hover:text-[#1B3A8A] transition-colors">
            {opp.source_url ? (
              <a href={opp.source_url} target="_blank" rel="noopener noreferrer"
                 className="hover:underline" onClick={(e) => e.stopPropagation()}
                 data-testid={`pwf-card-title-${opp.id}`}>
                {opp.title}
              </a>
            ) : opp.title}
          </h3>
          <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1 truncate" title={opp.agency}>{opp.agency}</div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border inline-flex items-center gap-1 ${fitClass}`}
              data-testid={`pwf-card-fit-${opp.id}`}>
          <Sparkles className="w-3 h-3" /> Fit {fit}
        </span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border inline-flex items-center gap-1 ${deadlineClass}`}
              data-testid={`pwf-card-deadline-${opp.id}`}>
          <Clock className="w-3 h-3" /> {days === null ? "—" : days < 0 ? `${Math.abs(days)}d ${isPt?"atrás":"ago"}` : `${days}d ${isPt?"restantes":"left"}`}
        </span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border inline-flex items-center gap-1 ${relMeta.cls}`}>
          {relMeta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11.5px] text-[var(--al-text-muted)] mb-3">
        <span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /> {opp.state || "—"}{opp.city ? ` · ${opp.city}` : ""}</span>
        <span className="inline-flex items-center gap-1 truncate"><Briefcase className="w-3 h-3 shrink-0" /> {opp.trade_category || (isPt?"Sem categoria":"Uncategorized")}</span>
        <span className="inline-flex items-center gap-1 truncate"><Calendar className="w-3 h-3 shrink-0" /> {fmtDate(opp.deadline)}</span>
        <span className="inline-flex items-center gap-1 truncate"><DollarSign className="w-3 h-3 shrink-0" /> {fmtMoney(opp.estimated_value)}</span>
      </div>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-[var(--al-border)]">
        <div className="text-[11px] text-[var(--al-text-muted)] truncate flex-1">
          {opp.contact_email
            ? <EmailAction email={opp.contact_email} isPt={isPt} context={govCtx(opp)} testid={`pwf-email-${opp.id}`} />
            : (isPt ? "Sem contato direto" : "No direct contact")}
        </div>
        <button
          type="button" data-testid={`pwf-view-${opp.id}`}
          onClick={() => onView(opp)}
          className="text-[12px] font-bold text-[#1B3A8A] hover:underline inline-flex items-center gap-1 ml-2 shrink-0"
        >
          {isPt ? "Detalhes" : "Details"} <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function DetailModal({ opp, onClose, isPt, onSaved }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAi(opp?.ai_summary || null); }, [opp?.id]);

  if (!opp) return null;

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/public-works/opportunities/${opp.id}/ai-summary`, {
        lang: isPt ? "pt" : "en",
      });
      setAi(data.ai);
    } catch (_e) { toast.error("AI failed"); }
    finally { setAiLoading(false); }
  };

  const saveToPipeline = async (stage = "interested") => {
    setSaving(true);
    try {
      await axios.post(`${API}/public-works/pipeline`, { opportunity_id: opp.id, stage });
      toast.success(isPt ? "Salvo no pipeline" : "Saved to pipeline");
      onSaved?.();
    } catch (_e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!opp} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pwf-detail-modal" className="max-w-3xl bg-white rounded-xl p-0 overflow-hidden border-[#E5E7EB]">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase font-bold tracking-wide text-[#1B3A8A] mb-1.5">
                {opp.opportunity_type} · {opp.source}
              </div>
              <DialogTitle className="text-[18px] font-bold text-[#111827] leading-tight">{opp.title}</DialogTitle>
              <p className="text-[12.5px] text-[#6B7280] mt-1">{opp.agency} · {opp.state} · {opp.city}</p>
            </div>
            <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded border ${URGENCY_COLORS[opp.urgency_level || "low"]}`}>
              {(opp.urgency_level || "low").toUpperCase()}
            </span>
          </div>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-5 text-[#111827]">
          <div className="grid sm:grid-cols-3 gap-4 text-[12.5px]">
            <DetailRow label={isPt ? "Prazo" : "Deadline"} value={fmtDate(opp.deadline)} />
            <DetailRow label={isPt ? "Valor estimado" : "Estimated value"} value={fmtMoney(opp.estimated_value)} />
            <DetailRow label="NAICS" value={opp.naics_code || "—"} />
            <DetailRow label="Fit score" value={`${opp.fit_score || 0}/100`} highlight />
            <DetailRow label={isPt ? "Categoria" : "Category"} value={opp.trade_category} />
            <DetailRow label={isPt ? "Postado em" : "Posted"} value={fmtDate(opp.posted_at)} />
          </div>

          <div>
            <h4 className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold mb-1.5">{isPt ? "Descrição" : "Description"}</h4>
            <p className="text-[13px] leading-relaxed">{opp.description}</p>
          </div>

          {(opp.contact_name || opp.contact_email || opp.contact_phone) && (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4">
              <h4 className="text-[11px] uppercase tracking-wide text-[#6B7280] font-semibold mb-2">{isPt ? "Contato" : "Point of contact"}</h4>
              <div className="text-[13px] space-y-0.5">
                {opp.contact_name && <div className="font-semibold">{opp.contact_name}</div>}
                {opp.contact_email && <div><EmailAction email={opp.contact_email} isPt={isPt} context={govCtx(opp)} testid="pwf-detail-email" /></div>}
                {opp.contact_phone && <div className="text-[#6B7280]"><PhoneAction phone={opp.contact_phone} isPt={isPt} testid="pwf-detail-phone" /></div>}
              </div>
            </div>
          )}

          <div className="bg-gradient-to-br from-[#1B3A8A]/5 to-[#C9A24E]/10 border border-[#1B3A8A]/20 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h4 className="text-[11px] uppercase tracking-wide text-[#1B3A8A] font-bold mb-0.5 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> AI Lead Assistant
                </h4>
                <p className="text-[11.5px] text-[#6B7280]">
                  {isPt ? "Resumo, riscos e recomendação Bid/No-Bid via GPT" : "AI summary, risks and Bid/No-Bid recommendation"}
                </p>
              </div>
              <button type="button" data-testid="pwf-ai-generate" onClick={generateAi} disabled={aiLoading}
                className="h-9 px-3 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
                {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {ai ? (isPt ? "Regenerar" : "Regenerate") : (isPt ? "Gerar análise" : "Generate analysis")}
              </button>
            </div>
            {ai ? (
              <div className="space-y-3 text-[13px]">
                <p>{ai.summary}</p>
                {ai.recommended_next_step && (
                  <div className="bg-white rounded p-2.5 border border-[#1B3A8A]/15">
                    <div className="text-[10px] uppercase tracking-wide text-[#6B7280] font-bold mb-1">{isPt ? "Próximo passo" : "Next step"}</div>
                    <div className="text-[12.5px]">{ai.recommended_next_step}</div>
                  </div>
                )}
                {ai.required_documents?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#6B7280] font-bold mb-1">{isPt ? "Documentos necessários" : "Required documents"}</div>
                    <ul className="text-[12.5px] list-disc list-inside space-y-0.5">{ai.required_documents.map((d, i) => <li key={`doc-${d || i}`}>{d}</li>)}</ul>
                  </div>
                )}
                {ai.risks?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-[#6B7280] font-bold mb-1 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3 text-amber-500" /> {isPt ? "Riscos" : "Risks"}</div>
                    <ul className="text-[12.5px] list-disc list-inside space-y-0.5">{ai.risks.map((r, i) => <li key={`risk-${r || i}`}>{r}</li>)}</ul>
                  </div>
                )}
                <div className="flex items-center gap-3 pt-2 border-t border-[#1B3A8A]/15">
                  <span className={`text-[11px] font-bold px-2 py-1 rounded ${ai.bid_no_bid === "BID" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {ai.bid_no_bid === "BID" ? "✓ BID" : "✗ NO BID"}
                  </span>
                  <span className="text-[11.5px] text-[#6B7280]">Fit: {ai.fit_score}/100</span>
                </div>
              </div>
            ) : (
              <p className="text-[12px] text-[#6B7280] italic">{isPt ? "Clique em 'Gerar análise' para receber resumo, riscos e recomendação." : "Click 'Generate analysis' to get a summary, risks and recommendation."}</p>
            )}
          </div>

          <MaterialsSection opp={opp} isPt={isPt} />
          <ResearchSection opp={opp} isPt={isPt} />
        </div>

        <div className="border-t border-[#E5E7EB] px-6 py-3 flex items-center gap-2 bg-[#F9FAFB]">
          <a href={portalHref(opp.title, opp.agency, opp.source_url)} target="_blank" rel="noopener noreferrer"
            data-testid="pwf-detail-source"
            className="h-9 px-3 rounded-lg border border-[#E5E7EB] hover:bg-white text-[12px] font-semibold inline-flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> {isPt ? "Fonte original" : "Original source"}
          </a>
          <button type="button" data-testid="pwf-detail-save"
            onClick={() => saveToPipeline("interested")} disabled={saving}
            className="ml-auto h-9 px-4 rounded-lg bg-[#C9A24E] hover:bg-[#D9B25E] text-[#0A1233] font-bold text-[12.5px] inline-flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {isPt ? "Salvar no pipeline" : "Save to pipeline"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#6B7280] font-semibold">{label}</div>
      <div className={`text-[13.5px] font-semibold mt-0.5 ${highlight ? "text-[#1B3A8A]" : ""}`}>{value}</div>
    </div>
  );
}

// --- Pipeline ---------------------------------------------------------------

function PipelineTab({ isPt }) {
  const [data, setData] = useState({ items: [], stages: [] });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/public-works/pipeline`); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const moveStage = async (oppId, stage) => {
    try {
      await axios.patch(`${API}/public-works/pipeline/${oppId}`, { stage });
      load();
    } catch (_e) { toast.error("Move failed"); }
  };

  if (loading) return <Spinner />;

  const stages = data.stages || Object.keys(STAGE_LABELS);
  const grouped = stages.reduce((acc, s) => ({ ...acc, [s]: [] }), {});
  (data.items || []).forEach((it) => {
    if (grouped[it.stage]) grouped[it.stage].push(it);
  });

  return (
    <div data-testid="pwf-pipeline" className="overflow-x-auto">
      <div className="flex gap-3 min-w-max pb-4">
        {stages.map((s) => {
          const label = STAGE_LABELS[s] || { en: s, pt: s, color: "bg-gray-100" };
          const items = grouped[s] || [];
          return (
            <div key={s} className="w-[280px] shrink-0 al-card p-3" data-testid={`pwf-stage-${s}`}>
              <div className={`flex items-center justify-between mb-2 px-2 py-1.5 rounded ${label.color}`}>
                <span className="text-[11px] font-bold uppercase tracking-wide">{isPt ? label.pt : label.en}</span>
                <span className="text-[11px] font-bold">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.length === 0 ? (
                  <p className="text-[11.5px] italic text-[var(--al-text-muted)] text-center py-4">{isPt ? "Sem itens" : "Empty"}</p>
                ) : items.map((it) => (
                  <div key={it.id} className="bg-[var(--al-bg)] border border-[var(--al-border)] rounded-md p-2.5 text-[12px]">
                    <div className="font-semibold line-clamp-2 leading-tight">{it.opportunity_snapshot?.title || it.opportunity_id}</div>
                    <div className="text-[10.5px] text-[var(--al-text-muted)] mt-1">
                      {it.opportunity_snapshot?.state} · {fmtMoney(it.opportunity_snapshot?.estimated_value)}
                    </div>
                    <select
                      value={it.stage} onChange={(e) => moveStage(it.opportunity_id, e.target.value)}
                      data-testid={`pwf-pipeline-move-${it.opportunity_id}`}
                      className="w-full mt-2 al-input h-7 text-[11px]"
                    >
                      {stages.map((sg) => <option key={sg} value={sg}>{(STAGE_LABELS[sg] || {})[isPt ? "pt" : "en"] || sg}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Alerts -----------------------------------------------------------------

function AlertsTab({ isPt, meta }) {
  const [alerts, setAlerts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "", state: "", trade_category: "", keyword: "", naics_code: "",
    deadline_range_days: 30, frequency: "weekly", minimum_fit_score: 0,
  });

  const load = async () => {
    const { data } = await axios.get(`${API}/public-works/alerts`);
    setAlerts(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setCreating(true);
    try {
      await axios.post(`${API}/public-works/alerts`, form);
      toast.success(isPt ? "Alerta criado" : "Alert created");
      setForm({ name: "", state: "", trade_category: "", keyword: "", naics_code: "",
                deadline_range_days: 30, frequency: "weekly", minimum_fit_score: 0 });
      load();
    } catch (_e) { toast.error("Failed"); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(isPt ? "Excluir alerta?" : "Delete alert?")) return;
    await axios.delete(`${API}/public-works/alerts/${id}`);
    load();
  };

  const [running, setRunning] = useState({}); // id -> bool
  const runNow = async (id) => {
    setRunning((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/public-works/alerts/${id}/run`, {}, { timeout: 180000 });
      const r = data.result || {};
      toast.success(isPt
        ? `Alerta executado: ${r.inserted || 0} novas, ${r.updated || 0} atualizadas`
        : `Alert ran: ${r.inserted || 0} new, ${r.updated || 0} updated`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <form onSubmit={create} data-testid="pwf-alerts-form" className="al-card p-5 lg:col-span-1 space-y-3">
        <h3 className="text-[14px] font-bold mb-1">{isPt ? "Novo alerta" : "New alert"}</h3>
        <TextInput label={isPt ? "Nome" : "Name"} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="pwf-alert-name" />
        <SelectInput label={isPt ? "Estado" : "State"} value={form.state} onChange={(v) => setForm({ ...form, state: v })} options={["", ...(meta.priority_states || []), ...(meta.secondary_states || [])]} testid="pwf-alert-state" />
        <SelectInput label={isPt ? "Trade" : "Trade"} value={form.trade_category} onChange={(v) => setForm({ ...form, trade_category: v })} options={["", ...(meta.trades || [])]} testid="pwf-alert-trade" />
        <TextInput label={isPt ? "Palavra-chave" : "Keyword"} value={form.keyword} onChange={(v) => setForm({ ...form, keyword: v })} testid="pwf-alert-keyword" />
        <TextInput label={isPt ? "Código NAICS (opcional)" : "NAICS code (optional)"}
          value={form.naics_code}
          onChange={(v) => setForm({ ...form, naics_code: v.replace(/\D/g, "").slice(0, 6) })}
          testid="pwf-alert-naics" />
        <SelectInput label={isPt ? "Frequência" : "Frequency"} value={form.frequency} onChange={(v) => setForm({ ...form, frequency: v })}
          options={[{ v: "daily", label: isPt ? "Diário" : "Daily" }, { v: "weekly", label: isPt ? "Semanal" : "Weekly" }, { v: "realtime", label: isPt ? "Tempo real" : "Real-time" }]} testid="pwf-alert-freq" />
        <button type="submit" disabled={creating || !form.name}
          data-testid="pwf-alert-submit"
          className="w-full h-10 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {isPt ? "Criar alerta" : "Create alert"}
        </button>
      </form>
      <div className="lg:col-span-2 space-y-3" data-testid="pwf-alerts-list">
        {alerts.length === 0 ? (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
            {isPt ? "Nenhum alerta criado ainda." : "No alerts yet."}
          </div>
        ) : alerts.map((a) => (
          <div key={a.id} className="al-card p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#1B3A8A]/10 text-[#1B3A8A] flex items-center justify-center">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold">{a.name}</div>
              <div className="text-[11.5px] text-[var(--al-text-muted)] mt-0.5">
                {[a.state, a.trade_category, a.keyword, `${a.deadline_range_days}d`].filter(Boolean).join(" · ")}
                <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--al-row-alt)] uppercase">{a.frequency}</span>
                <span className={`ml-1 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${a.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
              </div>
              {a.last_run_at && (
                <div className="text-[10.5px] text-[var(--al-text-muted)] mt-1">
                  {isPt ? "Última execução" : "Last run"}: {new Date(a.last_run_at).toLocaleString()} ·
                  <span className={a.last_run_status === "ok" ? "text-emerald-600 ml-1" : "text-rose-500 ml-1"}>
                    {a.last_run_status === "ok"
                      ? `+${a.last_run_inserted || 0} ${isPt ? "novas" : "new"}`
                      : (a.last_run_error || "error").slice(0, 60)}
                  </span>
                </div>
              )}
            </div>
            <button type="button" onClick={() => runNow(a.id)} disabled={!!running[a.id]}
              data-testid={`pwf-alert-run-${a.id}`}
              title={isPt ? "Buscar agora" : "Run now"}
              className="h-9 px-3 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
              {running[a.id]
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {isPt ? "Rodando…" : "Running…"}</>
                : <><Sparkles className="w-3.5 h-3.5" /> {isPt ? "Rodar" : "Run"}</>}
            </button>
            <button type="button" onClick={() => remove(a.id)}
              data-testid={`pwf-alert-delete-${a.id}`}
              className="w-9 h-9 rounded-lg border border-[var(--al-border)] hover:bg-rose-50 hover:text-rose-600 inline-flex items-center justify-center">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Sources ----------------------------------------------------------------

function SourcesTab({ isPt }) {
  const [list, setList] = useState([]);
  const [health, setHealth] = useState(null);
  const [hcLoading, setHcLoading] = useState(false);

  const loadHealth = async (force = false) => {
    setHcLoading(true);
    try {
      const { data } = await axios.get(`${API}/public-works/sources/healthcheck`,
        { params: force ? { force: 1 } : {} });
      setHealth(data);
    } catch (e) { /* silent — healthcheck is auxiliary */ }
    finally { setHcLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/public-works/sources`).then(({ data }) => setList(data.sources || [])).catch(() => {});
    loadHealth();
  }, []);

  // Per-status visual config. Live healthcheck status takes precedence over
  // the static `s.status` from /sources so the user sees actual reality.
  const healthBadge = (s) => {
    const hc = health?.sources?.[s.key || s.name?.toLowerCase()];
    if (hc) {
      const map = {
        ok:              { label: isPt ? "Operacional"        : "Operational",       cls: "bg-emerald-500 text-white",        dot: "#10B981" },
        degraded:        { label: isPt ? "Operacional (fallback)" : "Operational (fallback)", cls: "bg-amber-500 text-white", dot: "#F59E0B" },
        quota_exceeded:  { label: isPt ? "Cota esgotada"      : "Quota exhausted",   cls: "bg-rose-600 text-white",            dot: "#E11D48" },
        blocked:         { label: isPt ? "Bloqueado"          : "Blocked",           cls: "bg-rose-600 text-white",            dot: "#E11D48" },
        not_configured:  { label: isPt ? "Não configurado"    : "Not configured",    cls: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", dot: "#94A3B8" },
        planned:         { label: isPt ? "Planejado"          : "Planned",           cls: "bg-amber-100 text-amber-700",       dot: "#FBBF24" },
        future:          { label: isPt ? "Em breve"           : "Future",            cls: "bg-gray-100 text-gray-700",         dot: "#9CA3AF" },
      };
      return { ...(map[hc.status] || map.future), reason: hc.reason, status: hc.status };
    }
    // Fall back to the static map for sources without a probe.
    const map = {
      connected: { label: isPt ? "Conectado" : "Connected", cls: "bg-emerald-500 text-white" },
      ready_for_api: { label: isPt ? "Pronto p/ API" : "Ready for API", cls: "bg-emerald-100 text-emerald-700" },
      planned: { label: isPt ? "Planejado" : "Planned", cls: "bg-amber-100 text-amber-700" },
      future: { label: isPt ? "Em breve" : "Future", cls: "bg-gray-100 text-gray-700" },
    };
    return map[s.status] || { label: s.status, cls: "bg-gray-100" };
  };

  return (
    <div data-testid="pwf-sources">
      {/* Healthcheck summary bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 px-1">
        <div className="text-[12px] text-[var(--al-text-muted)]">
          {isPt ? "Healthcheck em tempo real:" : "Live healthcheck:"}
          {health?.as_of && (
            <span className="ml-1.5 font-mono-tech text-[10.5px]">
              {new Date(health.as_of).toLocaleTimeString()}
              {health.cached ? (isPt ? " (cache 60s)" : " (60s cache)") : ""}
            </span>
          )}
        </div>
        <button onClick={() => loadHealth(true)} disabled={hcLoading}
          data-testid="pwf-hc-refresh"
          className="ml-auto h-8 px-3 rounded-md border border-[var(--al-border)] text-[11.5px] font-bold inline-flex items-center gap-1.5 hover:bg-[var(--al-card-bg)] disabled:opacity-40">
          {hcLoading
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <RefreshCw className="w-3.5 h-3.5" />}
          {isPt ? "Re-checar agora" : "Re-check now"}
        </button>
      </div>

      {/* Daily Auto-Sync config */}
      <SamGovAutoSyncPanel isPt={isPt} />

      <div className="grid md:grid-cols-2 gap-4">
        {list.map((s) => {
          const b = healthBadge(s);
          const isSamgov = s.key === "samgov";
          return (
            <div key={s.name}
              data-testid={`pwf-source-${(s.key || s.name).toString().toLowerCase()}`}
              className="al-card p-5"
              style={b.dot ? { borderLeft: `4px solid ${b.dot}` } : undefined}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h4 className="text-[15px] font-bold flex items-center gap-2">
                    {b.dot && <span className="inline-block w-2 h-2 rounded-full" style={{ background: b.dot }} />}
                    {s.name}
                  </h4>
                  <span className="text-[10.5px] text-[var(--al-text-muted)] uppercase tracking-wide font-semibold">{s.tier}</span>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${b.cls}`}>{b.label}</span>
              </div>
              <p className="text-[12.5px] text-[var(--al-text-muted)]">{s.notes}</p>
              {/* Live healthcheck reason (only when probe ran) */}
              {b.reason && (
                <p className="mt-2 text-[11px] leading-snug rounded p-2"
                   style={{
                     background: b.status === "ok" ? "rgba(16,185,129,0.08)"
                       : b.status === "degraded" ? "rgba(245,158,11,0.08)"
                       : (b.status === "blocked" || b.status === "quota_exceeded") ? "rgba(225,29,72,0.08)"
                       : "rgba(148,163,184,0.08)",
                     color: "var(--al-text)",
                   }}>
                  <span className="font-bold mr-1">·</span>{b.reason}
                </p>
              )}
              {s.url && (
                <a href={s.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11.5px] text-[#1B3A8A] mt-2 hover:underline">
                  <ExternalLink className="w-3 h-3" /> {s.url.replace(/^https?:\/\//, "")}
                </a>
              )}
              {/* Apify billing CTA when quota exceeded */}
              {b.status === "quota_exceeded" && (
                <a href="https://console.apify.com/billing" target="_blank" rel="noreferrer"
                   data-testid={`pwf-source-${s.key}-apify-billing`}
                   className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[11.5px] font-bold">
                  <ExternalLink className="w-3.5 h-3.5" />
                  {isPt ? "Adicionar saldo Apify" : "Top up Apify billing"}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Common -----------------------------------------------------------------

function TextInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1 block">{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-9 px-2.5 rounded-md bg-[var(--al-bg)] border border-[var(--al-border)] focus:border-[#1B3A8A] outline-none text-[12.5px]" />
    </label>
  );
}

function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-9 px-2.5 rounded-md bg-[var(--al-bg)] border border-[var(--al-border)] focus:border-[#1B3A8A] outline-none text-[12.5px]">
        {options.map((o, i) => {
          const v = typeof o === "string" ? o : o.v;
          const lbl = typeof o === "string" ? (o || "—") : o.label;
          return <option key={`opt-${v}`} value={v}>{lbl}</option>;
        })}
      </select>
    </label>
  );
}

function Spinner() {
  return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1B3A8A]" /></div>;
}

// Suppress unused imports lint:
void Building2;


// --- SIC & NAICS tab --------------------------------------------------------

function SicNaicsTab({ isPt, onTrial }) {
  const [kind, setKind] = useState("naics");
  const [include, setInclude] = useState([]);
  const [exclude, setExclude] = useState([]);
  const [query, setQuery] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [allCodes, setAllCodes] = useState([]);
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public-works/sic-naics/codes?kind=${kind}&q=${encodeURIComponent(query)}&limit=200`)
      .then(({ data }) => setAllCodes(data.items || [])).catch(() => setAllCodes([]));
  }, [kind, query]);

  const toggle = (list, setList, code) => {
    setList(list.includes(code) ? list.filter((c) => c !== code) : [...list, code]);
  };

  const addManualCode = (target) => {
    const v = (manualCode || "").trim();
    if (!v) return;
    if (!/^\d{3,6}$/.test(v)) {
      toast.error(isPt ? "Código deve ter de 3 a 6 dígitos" : "Code must be 3 to 6 digits");
      return;
    }
    if (target === "include") {
      if (!include.includes(v)) setInclude([...include, v]);
    } else if (!exclude.includes(v)) setExclude([...exclude, v]);
    setManualCode("");
  };

  const runSearch = async () => {
    setSearching(true);
    try {
      const body = {
        include_naics: kind === "naics" ? include : [],
        exclude_naics: kind === "naics" ? exclude : [],
        include_sic: kind === "sic" ? include : [],
        exclude_sic: kind === "sic" ? exclude : [],
        include_nigp: kind === "nigp" ? include : [],
        exclude_nigp: kind === "nigp" ? exclude : [],
        limit: 100,
      };
      const { data } = await axios.post(`${API}/public-works/sic-naics/search`, body);
      setItems(data.items || []);
      if ((data.items || []).length === 0) {
        toast.info(isPt ? "Nenhuma oportunidade com esses códigos. Rode 'Sync SAM.gov' antes." : "No opportunities with these codes. Run 'Sync SAM.gov' first.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSearching(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="pwf-sicnaics">
      <aside className="al-card p-5 space-y-4 lg:col-span-1">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-[#1B3A8A]" />
          <h3 className="text-[15px] font-bold">{isPt ? "SIC, NAICS e NIGP" : "SIC, NAICS and NIGP"}</h3>
        </div>

        <div className="grid grid-cols-3 rounded-lg border border-[var(--al-border)] overflow-hidden p-1 bg-[var(--al-row-alt)]">
          {["sic", "naics", "nigp"].map((k) => (
            <button key={k} type="button" onClick={() => { setKind(k); setInclude([]); setExclude([]); setItems([]); }}
              data-testid={`pwf-sicnaics-tab-${k}`}
              className={`py-1.5 text-[12px] font-bold uppercase rounded ${kind === k ? "bg-[#1B3A8A] text-white shadow" : "text-[var(--al-text-muted)]"}`}>
              {k.toUpperCase()}
            </button>
          ))}
        </div>

        <input
          type="text" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder={isPt ? `Buscar códigos ${kind.toUpperCase()}...` : `Search ${kind.toUpperCase()} codes...`}
          data-testid="pwf-sicnaics-query"
          className="w-full al-input h-9 text-[12.5px]"
        />

        {/* Manual code input — type any code (3-6 digits) and add directly */}
        <div className="rounded-lg border border-dashed border-[#C9A24E]/50 bg-[#C9A24E]/5 p-2.5 space-y-2"
             data-testid="pwf-sicnaics-manual">
          <div className="text-[10.5px] uppercase tracking-wide font-bold text-[#92660C] inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> {isPt ? "Adicionar código manualmente" : "Add code manually"}
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManualCode("include"); } }}
              placeholder={kind === "nigp" ? "77093" : kind === "sic" ? "1761" : "238160"}
              data-testid="pwf-sicnaics-manual-input"
              className="flex-1 al-input h-9 text-[13px] font-mono tabular-nums"
            />
            <button type="button" onClick={() => addManualCode("include")}
              data-testid="pwf-sicnaics-manual-include"
              title={isPt ? "Incluir" : "Include"}
              className="h-9 px-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1">
              + {isPt ? "Incluir" : "Inc."}
            </button>
            <button type="button" onClick={() => addManualCode("exclude")}
              data-testid="pwf-sicnaics-manual-exclude"
              title={isPt ? "Excluir" : "Exclude"}
              className="h-9 px-2.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-[11.5px] font-bold inline-flex items-center gap-1">
              − {isPt ? "Excluir" : "Exc."}
            </button>
          </div>
          <p className="text-[10px] text-[#92660C]/80">
            {isPt
              ? "Digite o código exato (3 a 6 dígitos) e pressione Enter ou clique em Incluir/Excluir."
              : "Type the exact code (3-6 digits) and press Enter or click Include/Exclude."}
          </p>
        </div>

        <CodeSelectorList
          label={isPt ? "Incluir" : "Include"}
          codes={allCodes} selected={include} onToggle={(c) => toggle(include, setInclude, c)}
          accent="emerald" testid="pwf-sicnaics-include"
        />
        <CodeSelectorList
          label={isPt ? "Excluir" : "Exclude"}
          codes={allCodes.filter((c) => !include.includes(c.code))}
          selected={exclude} onToggle={(c) => toggle(exclude, setExclude, c)}
          accent="rose" testid="pwf-sicnaics-exclude"
        />

        <button type="button" onClick={runSearch} disabled={searching || (include.length === 0 && exclude.length === 0)}
          data-testid="pwf-sicnaics-search"
          className="w-full h-10 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {isPt ? "Buscar oportunidades" : "Search opportunities"}
        </button>

        <div className="pt-3 border-t border-[var(--al-border)] space-y-1.5 text-[12px]">
          <a href="https://www.osha.gov/data/sic-manual" target="_blank" rel="noopener noreferrer"
             data-testid="pwf-sic-extlist"
             className="inline-flex items-center gap-1.5 text-[#1B3A8A] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> {isPt ? "Lista oficial SIC (OSHA)" : "Official SIC list (OSHA)"}
          </a><br/>
          <a href="https://www.census.gov/naics/" target="_blank" rel="noopener noreferrer"
             data-testid="pwf-naics-extlist"
             className="inline-flex items-center gap-1.5 text-[#1B3A8A] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> {isPt ? "Lista oficial NAICS (Census)" : "Official NAICS list (Census)"}
          </a><br/>
          <a href="https://www.nigp.org/home/find-procurement-resources/nigp-code/" target="_blank" rel="noopener noreferrer"
             data-testid="pwf-nigp-extlist"
             className="inline-flex items-center gap-1.5 text-[#1B3A8A] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> {isPt ? "Lista oficial NIGP" : "Official NIGP list"}
          </a>
        </div>
      </aside>

      <div className="lg:col-span-2 space-y-3">
        <div className="al-card p-4">
          <div className="text-[11px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-2">
            {isPt ? "Códigos selecionados" : "Selected codes"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {include.length === 0 && exclude.length === 0 && (
              <span className="text-[12px] text-[var(--al-text-muted)] italic">
                {isPt ? "Selecione códigos à esquerda para começar." : "Pick codes on the left to start."}
              </span>
            )}
            {include.map((c) => (
              <span key={`in-${c}`} className="text-[11px] font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                + {c}
                <button type="button" onClick={() => setInclude(include.filter((x) => x !== c))} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {exclude.map((c) => (
              <span key={`ex-${c}`} className="text-[11px] font-bold px-2 py-1 rounded bg-rose-100 text-rose-700 inline-flex items-center gap-1">
                − {c}
                <button type="button" onClick={() => setExclude(exclude.filter((x) => x !== c))} className="hover:text-emerald-600"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3" data-testid="pwf-sicnaics-results">
          {items.length === 0 ? (
            <div className="al-card p-10 text-center text-[var(--al-text-muted)] sm:col-span-2">
              {isPt ? "Nenhum resultado ainda. Selecione códigos e clique em Buscar." : "No results yet. Pick codes and click Search."}
            </div>
          ) : items.map((opp) => (
            <a key={opp.id} href={portalHref(opp.title, opp.agency, opp.source_url)} target="_blank" rel="noopener noreferrer"
               className="al-card p-4 hover:shadow-lg hover:border-[#1B3A8A]/40 transition-all">
              <div className="text-[10px] uppercase font-bold text-[#1B3A8A] mb-1">{opp.source} · NAICS {opp.naics_code || "—"}</div>
              <div className="text-[13.5px] font-bold leading-snug line-clamp-2">{opp.title}</div>
              <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1 truncate">{opp.agency}</div>
              <div className="flex items-center gap-2 mt-2 text-[11px] text-[var(--al-text-muted)]">
                <MapPin className="w-3 h-3" /> {opp.state || "—"}
                <span className="ml-auto">{(opp.deadline || "").slice(0, 10)}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function CodeSelectorList({ label, codes, selected, onToggle, accent, testid }) {
  const tone = accent === "emerald"
    ? "bg-emerald-100 text-emerald-700 border-emerald-300"
    : "bg-rose-100 text-rose-700 border-rose-300";
  return (
    <div data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">{label}</div>
      <div className="max-h-44 overflow-y-auto rounded-lg border border-[var(--al-border)] bg-[var(--al-bg)]">
        {codes.length === 0 && (
          <div className="text-[11.5px] text-[var(--al-text-muted)] p-3 italic">—</div>
        )}
        {codes.slice(0, 60).map((c) => {
          const on = selected.includes(c.code);
          return (
            <button key={c.code} type="button" onClick={() => onToggle(c.code)}
              className={`w-full text-left px-3 py-1.5 text-[11.5px] flex items-center gap-2 hover:bg-[var(--al-row-alt)] ${on ? "font-bold" : ""}`}>
              <span className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] tabular-nums ${on ? tone : "bg-[var(--al-row-alt)] text-[var(--al-text-muted)] border-[var(--al-border)]"}`}>
                {c.code}
              </span>
              <span className="truncate">{c.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Construction Leads tab -------------------------------------------------

function ConstructionLeadsTab({ isPt, onTrial }) {
  const [providers, setProviders] = useState({});
  const [form, setForm] = useState({
    segment: "residential",
    location: "Florida",
    project_types: [],
    statuses: [],
    min_valuation: 100000,
    max_results: 25,
    naics_codes: [],
  });
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    axios.get(`${API}/construction-leads/providers`).then(({ data }) => setProviders(data.providers || {})).catch(() => {});
  }, []);

  const togglePT = (p) => setForm((f) => ({ ...f, project_types: f.project_types.includes(p) ? f.project_types.filter((x) => x !== p) : [...f.project_types, p] }));
  const toggleST = (s) => setForm((f) => ({ ...f, statuses: f.statuses.includes(s) ? f.statuses.filter((x) => x !== s) : [...f.statuses, s] }));

  // Segment → provider mapping (mirrors backend routes/construction_leads.py:
  // commercial → dodge|constructconnect, residential → buildzoom, public → samgov).
  // We compute readiness from the /providers endpoint so the user gets a
  // visible "Needs key" badge *before* hitting Search — no more dead-end
  // toast errors when Dodge/ConstructConnect keys aren't configured.
  const SEGMENT_PROVIDERS = {
    commercial:  ["dodge", "constructconnect"],
    residential: ["buildzoom"],
    public:      ["samgov"],
  };
  const isSegmentReady = (seg) =>
    (SEGMENT_PROVIDERS[seg] || []).some((k) => (providers[k] || {}).status === "ready");
  const currentSegmentReady = isSegmentReady(form.segment);

  const PROJECT_TYPES = {
    commercial: ["Office", "Retail", "Warehouse", "Hospitality", "Mixed Use", "Healthcare"],
    residential: ["Single Family", "Multi-family", "Renovations", "ADU", "Pool"],
    public: ["Roads", "Bridges", "Schools", "Parks"],
  };
  const STATUSES = ["Permit Issued", "Under Review", "Planning Phase"];

  const run = async () => {
    setSearching(true);
    try {
      const { data } = await axios.post(`${API}/construction-leads/search`, form, { timeout: 180000 });
      setItems(data.items || []);
      toast.success(`${data.count || 0} leads · ${data.provider}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSearching(false); }
  };

  const exportCsv = () => {
    if (blockExportIfTrial(onTrial, isPt ? "exportar CSV" : "export CSV")) return;
    if (items.length === 0) return;
    const header = ["lead_id","source","segment","project_name","address","city","state","valuation","lead_score","owner_name","contractor_name","contact_email","contact_phone","source_url"];
    const rows = items.map((x) => [
      x.lead_id, x.source, x.segment, x.project_name, x.address, x.city, x.state, x.valuation, x.lead_score,
      x.owner_name, x.contractor_name, (x.contact_info || {}).email, (x.contact_info || {}).phone, x.source_url,
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `construction_leads_${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="pwf-construction">
      <aside className="al-card p-5 space-y-4 lg:col-span-1">
        <div className="flex items-center gap-2">
          <Hammer className="w-4 h-4 text-[#1B3A8A]" />
          <h3 className="text-[15px] font-bold">{isPt ? "Leads de Obras" : "Construction Leads"}</h3>
        </div>

        <div className="grid grid-cols-3 rounded-lg border border-[var(--al-border)] p-1 bg-[var(--al-row-alt)]">
          {["commercial","residential","public"].map((s) => {
            const ready = isSegmentReady(s);
            return (
              <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, segment: s, project_types: [] }))}
                data-testid={`pwf-cl-segment-${s}`}
                className={`relative py-1.5 text-[11.5px] font-bold uppercase rounded ${form.segment === s ? "bg-[#1B3A8A] text-white shadow" : "text-[var(--al-text-muted)]"}`}>
                {s === "commercial" ? (isPt ? "Comercial" : "Commercial")
                 : s === "residential" ? (isPt ? "Residencial" : "Residential")
                 : (isPt ? "Pública" : "Public")}
                {/* Tiny dot in the tab itself: green = ready, amber = needs key. */}
                <span data-testid={`pwf-cl-segment-${s}-dot`}
                      className={`absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full ${ready ? "bg-emerald-400" : "bg-amber-400"}`}
                      title={ready ? "Ready" : (isPt ? "Precisa de chave de API" : "API key required")} />
              </button>
            );
          })}
        </div>

        <TextInput label={isPt ? "Localização (estado/cidade)" : "Location (state/city)"}
          value={form.location} onChange={(v) => setForm({ ...form, location: v })}
          testid="pwf-cl-location" />

        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
            {isPt ? "Tipo de obra" : "Project type"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(PROJECT_TYPES[form.segment] || []).map((p) => {
              const on = form.project_types.includes(p);
              return (
                <button key={p} type="button" onClick={() => togglePT(p)}
                  className={`text-[10.5px] font-semibold px-2 py-1 rounded-full border ${on ? "bg-[#1B3A8A] text-white border-[#1B3A8A]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#1B3A8A]"}`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">Status</div>
          <div className="flex flex-wrap gap-1.5">
            {STATUSES.map((s) => {
              const on = form.statuses.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggleST(s)}
                  className={`text-[10.5px] font-semibold px-2 py-1 rounded-full border ${on ? "bg-[#10B981] text-white border-[#10B981]" : "border-[var(--al-border)] text-[var(--al-text-muted)] hover:border-[#10B981]"}`}>
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
            {isPt ? "Valor mínimo (USD)" : "Min valuation (USD)"}
          </span>
          <input type="number" min="0" step="50000" value={form.min_valuation}
            onChange={(e) => setForm({ ...form, min_valuation: Number(e.target.value) })}
            data-testid="pwf-cl-minval"
            className="w-full mt-1 al-input h-9 text-[12.5px] tabular-nums" />
        </label>

        {/* NAICS code filter — only meaningful on the public/SAM.gov segment. */}
        {form.segment === "public" && (
          <div data-testid="pwf-cl-naics-section">
            <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">
              {isPt ? "Códigos NAICS (opcional)" : "NAICS codes (optional)"}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {form.naics_codes.length === 0 ? (
                <span className="text-[10.5px] italic text-[var(--al-text-muted)]">
                  {isPt ? "Sem filtro NAICS — todos os resultados públicos." : "No NAICS filter — all public results."}
                </span>
              ) : form.naics_codes.map((c) => (
                <span key={`naics-${c}`} className="text-[10.5px] font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                  {c}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, naics_codes: f.naics_codes.filter((x) => x !== c) }))}
                    className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-1.5">
              <input
                type="text" placeholder="238160"
                data-testid="pwf-cl-naics-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = e.currentTarget.value.replace(/\D/g, "").slice(0, 6);
                    if (v && !form.naics_codes.includes(v)) {
                      setForm((f) => ({ ...f, naics_codes: [...f.naics_codes, v] }));
                      e.currentTarget.value = "";
                    }
                  }
                }}
                className="flex-1 al-input h-9 text-[12.5px] font-mono tabular-nums" />
              <span className="text-[10px] text-[var(--al-text-muted)] italic self-center">
                {isPt ? "Enter para adicionar" : "Enter to add"}
              </span>
            </div>
          </div>
        )}

        <button type="button" onClick={run} disabled={searching || !currentSegmentReady}
          data-testid="pwf-cl-run"
          className="w-full h-10 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
          {isPt ? "Buscar leads" : "Search leads"}
        </button>

        {/* Pre-flight segment readiness — surfaces the "needs API key" state
            BEFORE the user hits Search. Replaces the previous dead-end toast. */}
        {!currentSegmentReady && Object.keys(providers).length > 0 && (
          <div data-testid="pwf-cl-segment-warn"
            className="rounded-lg border border-amber-400/60 bg-amber-50 px-3 py-2.5 text-[11.5px] leading-snug"
            style={{ color: "#92400E" }}>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
              <div className="space-y-1.5">
                <div className="font-bold">
                  {isPt
                    ? `O segmento ${form.segment} requer uma chave de API.`
                    : `The ${form.segment} segment requires an API key.`}
                </div>
                <div>
                  {isPt
                    ? "Configure Dodge ou ConstructConnect em Integrações, ou troque para um segmento pronto:"
                    : "Save a Dodge or ConstructConnect key in Integrations, or switch to a ready segment:"}
                </div>
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {["residential","public"].filter(isSegmentReady).map((s) => (
                    <button key={s} type="button"
                      onClick={() => setForm((f) => ({ ...f, segment: s, project_types: [] }))}
                      data-testid={`pwf-cl-segment-switch-${s}`}
                      className="px-2 py-0.5 rounded-md border border-amber-500/60 hover:bg-amber-100 font-bold uppercase tracking-wide">
                      {s === "residential"
                        ? (isPt ? "Usar Residencial (BuildZoom)" : "Use Residential (BuildZoom)")
                        : (isPt ? "Usar Pública (SAM.gov)" : "Use Public (SAM.gov)")}
                    </button>
                  ))}
                  <a href="/integrations" data-testid="pwf-cl-link-integrations"
                     className="px-2 py-0.5 rounded-md border border-amber-500/60 hover:bg-amber-100 font-bold uppercase tracking-wide">
                    {isPt ? "Abrir Integrações →" : "Open Integrations →"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-[var(--al-border)] text-[11px] text-[var(--al-text-muted)] space-y-1.5">
          <div className="font-semibold uppercase text-[10px] tracking-wide">{isPt ? "Fontes (logic gate)" : "Sources (logic gate)"}</div>
          {Object.entries(providers).map(([k, p]) => (
            <div key={k} className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${p.status === "ready" ? "bg-emerald-500" : "bg-amber-500"}`} />
              <span className="font-medium">{p.name}</span>
              <span className="text-[10px] uppercase tracking-wide ml-auto">{p.status === "ready" ? (isPt ? "Pronto" : "Ready") : (isPt ? "Precisa chave" : "Needs key")}</span>
            </div>
          ))}
        </div>
      </aside>

      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center gap-2">
          <h4 className="text-[14px] font-bold">{isPt ? "Resultados" : "Results"}</h4>
          <span className="text-[12px] text-[var(--al-text-muted)]">{items.length}</span>
          <button type="button" onClick={exportCsv} disabled={items.length === 0}
            data-testid="pwf-cl-csv"
            className="ml-auto h-8 px-3 rounded-lg border border-[var(--al-border)] text-[11.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[var(--al-row-alt)] disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>

        {items.length === 0 ? (
          <div className="al-card p-10 text-center text-[var(--al-text-muted)]">
            {searching
              ? (isPt ? "Buscando…" : "Searching…")
              : (isPt ? "Nenhum resultado ainda. Configure filtros à esquerda e clique em Buscar." : "No results yet. Configure filters on the left and click Search.")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((x) => (
              <a key={x.lead_id} href={portalHref(x.project_name, x.source, x.source_url)} target="_blank" rel="noopener noreferrer"
                 className="al-card p-4 hover:shadow-lg hover:border-[#1B3A8A]/40 transition-all">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase font-bold text-[#1B3A8A]">{x.source} · {x.segment}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Score {x.lead_score}
                  </span>
                </div>
                <h4 className="text-[13.5px] font-bold leading-snug line-clamp-2">{x.project_name}</h4>
                <div className="text-[11.5px] text-[var(--al-text-muted)] mt-1 truncate">{x.address || "—"}</div>
                <div className="grid grid-cols-2 gap-1 mt-2 text-[11px] text-[var(--al-text-muted)]">
                  <span className="truncate">Owner: <b className="text-[var(--al-text)]">{x.owner_name || "—"}</b></span>
                  <span className="truncate">Contractor: <b className="text-[var(--al-text)]">{x.contractor_name || "—"}</b></span>
                  <span className="tabular-nums">${(x.valuation || 0).toLocaleString()}</span>
                  <span className="truncate text-right">{x.permit_status || ""}</span>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ──────────────────────────────────────────────────────────────────
   SAM.gov Daily Auto-Sync — config panel
   Rendered inside SourcesTab. Lets admins toggle the cron, set the
   time, NAICS/state/trade filter, and high-value threshold for Slack
   alerts. Includes a "Run now" button that triggers the same job
   path the scheduler would run at 06:00 UTC.
   ────────────────────────────────────────────────────────────────── */
function SamGovAutoSyncPanel({ isPt }) {
  const [cfg, setCfg] = useState(null);
  const [last, setLast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public-works/samgov-cron/config`);
      setCfg(data.config);
      setLast(data.last_run);
    } catch (e) {
      // Endpoint requires admin — silently hide for non-admins.
      setCfg(null);
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return null;
  const update = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/public-works/samgov-cron/config`, {
        enabled: cfg.enabled,
        hour_utc: cfg.hour_utc,
        minute_utc: cfg.minute_utc,
        posted_within_days: cfg.posted_within_days,
        limit: cfg.limit,
        naics: cfg.naics || "",
        state: cfg.state || "",
        trade: cfg.trade || "",
        high_value_threshold_usd: cfg.high_value_threshold_usd,
        slack_alert_enabled: cfg.slack_alert_enabled,
      });
      setCfg(data.config);
      toast.success(isPt ? "Configuração salva" : "Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/public-works/samgov-cron/run`);
      const apify = data?.result?.apify_fallback_used ? " (via Apify)" : "";
      toast.success(
        (isPt ? `Sync executado: ${data.inserted} novas, ${data.updated} atualizadas` :
                `Sync done: ${data.inserted} new, ${data.updated} updated`) + apify
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  const lastBadge = last?.finished_at && (
    <span className="text-[10.5px] text-[var(--al-text-muted)]">
      {isPt ? "Último run" : "Last run"}:{" "}
      <b className="font-mono-tech">{new Date(last.finished_at).toLocaleString()}</b>
      {" · "}
      <span className="text-emerald-600 dark:text-emerald-400 font-bold">{last.inserted || 0}</span> {isPt ? "novas" : "new"}
      {", "}
      <span className="text-blue-600 dark:text-blue-400 font-bold">{last.updated || 0}</span> {isPt ? "atualizadas" : "updated"}
      {(last.alerts_sent || 0) > 0 && (
        <>{" · "}<span className="text-amber-600 dark:text-amber-400 font-bold">🔔 {last.alerts_sent}</span> Slack</>
      )}
    </span>
  );

  return (
    <div className="mb-5 rounded-2xl border-2 al-card p-5"
         style={{ borderColor: cfg.enabled ? "#10B981" : "var(--al-border)" }}
         data-testid="samgov-cron-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-[15px] font-bold flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#1B3A8A]" />
            {isPt ? "Auto-Sync diário SAM.gov" : "SAM.gov Daily Auto-Sync"}
            {cfg.enabled
              ? <span className="text-[9.5px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-500 text-white">ON</span>
              : <span className="text-[9.5px] uppercase font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">OFF</span>}
          </h3>
          <p className="text-[12px] text-[var(--al-text-muted)] mt-1 max-w-2xl">
            {isPt
              ? "Cron diário às 06:00 UTC. Puxa novas bids federais (via Apify fallback se o gateway istio bloquear), insere no pipeline e dispara alerta Slack para oportunidades acima do threshold."
              : "Daily cron at 06:00 UTC. Pulls fresh federal bids (Apify fallback if the istio gateway blocks us), inserts them into the pipeline, and fires a Slack alert for opportunities above the threshold."}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
          <input type="checkbox" checked={cfg.enabled}
            data-testid="samgov-cron-enabled"
            onChange={(e) => update({ enabled: e.target.checked })}
            className="accent-emerald-500 w-4 h-4" />
          <span className="text-[12px] font-bold">{isPt ? "Ativar" : "Enable"}</span>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <CronField label={isPt ? "Hora UTC" : "Hour UTC"} type="number" v={cfg.hour_utc}
          onChange={(v) => update({ hour_utc: Math.max(0, Math.min(23, Number(v) || 0)) })}
          testid="samgov-cron-hour" hint="0–23" />
        <CronField label={isPt ? "Janela (dias)" : "Posted within (days)"} type="number" v={cfg.posted_within_days}
          onChange={(v) => update({ posted_within_days: Math.max(1, Math.min(14, Number(v) || 1)) })}
          testid="samgov-cron-days" hint="1–14" />
        <CronField label={isPt ? "Limite por busca" : "Per-run limit"} type="number" v={cfg.limit}
          onChange={(v) => update({ limit: Math.max(5, Math.min(200, Number(v) || 5)) })}
          testid="samgov-cron-limit" hint="5–200" />
        <CronField label={isPt ? "Estado (US)" : "State (US)"} v={cfg.state}
          onChange={(v) => update({ state: (v || "").toUpperCase().slice(0, 2) })}
          testid="samgov-cron-state" hint="FL, TX, CA, …" />
        <CronField label="NAICS" v={cfg.naics}
          onChange={(v) => update({ naics: v })}
          testid="samgov-cron-naics" hint="ex.: 238160" />
        <CronField label={isPt ? "Trade" : "Trade"} v={cfg.trade}
          onChange={(v) => update({ trade: v })}
          testid="samgov-cron-trade" hint="Roofing, Concrete…" />
        <CronField label={isPt ? "Alerta Slack ≥ US$" : "Slack alert ≥ US$"} type="number" v={cfg.high_value_threshold_usd}
          onChange={(v) => update({ high_value_threshold_usd: Math.max(0, Number(v) || 0) })}
          testid="samgov-cron-threshold" hint={isPt ? "0 desativa" : "0 disables"} />
        <label className="inline-flex items-end gap-2 cursor-pointer pb-2">
          <input type="checkbox" checked={cfg.slack_alert_enabled}
            data-testid="samgov-cron-slack-enabled"
            onChange={(e) => update({ slack_alert_enabled: e.target.checked })}
            className="accent-emerald-500 w-4 h-4" />
          <span className="text-[12px]">{isPt ? "Enviar p/ Slack" : "Send to Slack"}</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2 items-center pt-3 border-t border-[var(--al-border)]">
        <button onClick={save} disabled={saving}
          data-testid="samgov-cron-save"
          className="h-9 px-4 rounded-lg bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {isPt ? "Salvar configuração" : "Save settings"}
        </button>
        <button onClick={runNow} disabled={running}
          data-testid="samgov-cron-runnow"
          className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {isPt ? "Rodar agora" : "Run now"}
        </button>
        <div className="ml-auto">{lastBadge}</div>
      </div>
    </div>
  );
}

function CronField({ label, v, onChange, type = "text", testid, hint }) {
  return (
    <label className="block">
      <span className="text-[10.5px] font-bold uppercase tracking-wider text-[var(--al-text-muted)]">{label}</span>
      <input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={hint}
        className="mt-1 w-full bg-[var(--al-card-bg)] border border-[var(--al-border)] rounded-md px-2 h-8 text-[12.5px] focus:border-[#1B3A8A] outline-none" />
    </label>
  );
}

